/**
 * FocusMode.jsx — Rammy's immersive Focus overlay.
 *
 * Composes three primitives the user already loves:
 *   • generated LOOPING ambience (ElevenLabs sfx)
 *   • a serene animated backdrop
 *   • a countdown timer with a warm end-of-session moment
 *
 * This is intentionally separate from the sidebar "Focus" screen (which is
 * the productivity Pomodoro + inbox hold). Focus Mode is the *feeling*.
 */
import { useEffect, useRef, useState } from "react";
import * as L from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const DURATIONS = [
  { min: 15, label: "15m", copy: "A short breath." },
  { min: 25, label: "25m", copy: "Classic pomodoro." },
  { min: 45, label: "45m", copy: "Deep work." },
];

const AMBIENCES = [
  { key: "rain",    label: "Rain on porch",     prompt: "Gentle rain on a wooden porch, distant thunder, calm and steady, ambient loop" },
  { key: "cafe",    label: "Café hum",          prompt: "Warm café hum, low chatter, occasional espresso hiss, calm background loop" },
  { key: "forest",  label: "Forest at dawn",    prompt: "Deep forest at dawn, gentle bird calls, soft wind through leaves, ambient loop" },
  { key: "ocean",   label: "Ocean at night",    prompt: "Ocean waves rolling onto a quiet cove at night, gentle and rhythmic, ambient loop" },
  { key: "fire",    label: "Fireplace",         prompt: "Crackling fireplace in a wooden cabin, soft wind outside, cozy ambient loop" },
];

const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

export default function FocusMode({ user, onClose }) {
  const [phase, setPhase] = useState("setup"); // setup | preparing | running | done
  const [duration, setDuration] = useState(25);
  const [ambience, setAmbience] = useState("rain");
  const [audioUrl, setAudioUrl] = useState(null);
  const [remaining, setRemaining] = useState(0);
  const [paused, setPaused] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const audioRef = useRef(null);
  const tickRef = useRef(null);
  const endsAtRef = useRef(0);

  // Escape:
  //  · setup/done  → close overlay
  //  · running     → tuck it away so the ambience keeps playing while you work
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (phase === "setup" || phase === "done") onClose?.();
      else if (phase === "running" && !minimized) setMinimized(true);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [phase, minimized, onClose]);

  // Countdown tick.
  useEffect(() => {
    if (phase !== "running" || paused) {
      clearInterval(tickRef.current);
      return;
    }
    tickRef.current = setInterval(() => {
      const left = Math.max(0, Math.round((endsAtRef.current - Date.now()) / 1000));
      setRemaining(left);
      if (left <= 0) finishSession();
    }, 1000);
    return () => clearInterval(tickRef.current);
  }, [phase, paused]);

  // Pause/resume the audio in lockstep with the timer.
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    if (phase === "running" && !paused) a.play().catch(() => {});
    else a.pause();
  }, [phase, paused]);

  const start = async () => {
    setPhase("preparing");
    try {
      const preset = AMBIENCES.find(a => a.key === ambience) || AMBIENCES[0];
      // Ask ElevenLabs for a 22-second loopable clip. The <audio loop /> tag
      // then repeats it for the whole session — cheap on quota, no chaining.
      const { data } = await api.post("/create/sound", {
        prompt: preset.prompt,
        duration_seconds: 22,
        loop: true,
      });
      setAudioUrl(`data:${data.mime};base64,${data.audio_base64}`);
      endsAtRef.current = Date.now() + duration * 60 * 1000;
      setRemaining(duration * 60);
      setPhase("running");
    } catch (e) {
      console.warn("focus start failed:", e);
      toast.error("Couldn't prepare the ambience — try again?");
      setPhase("setup");
    }
  };

  const finishSession = () => {
    clearInterval(tickRef.current);
    const a = audioRef.current;
    if (a) { try { a.pause(); } catch (_) { /* no-op */ } }
    setPhase("done");
    setMinimized(false); // surface the celebration even if user was minimised
    // Soft chime via WebAudio — no network round-trip.
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = "sine"; o.frequency.value = 660;
      g.gain.setValueAtTime(0.0001, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.15, ctx.currentTime + 0.05);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 1.4);
      o.connect(g); g.connect(ctx.destination);
      o.start(); o.stop(ctx.currentTime + 1.5);
    } catch (_) { /* audio ctx unavailable, silent finish */ }
  };

  const endEarly = () => {
    if (confirm("End this session early?")) finishSession();
  };

  const saveMemory = async () => {
    const preset = AMBIENCES.find(a => a.key === ambience) || AMBIENCES[0];
    try {
      await api.post("/memories", {
        title: `${duration}-min focus · ${preset.label}`,
        detail: `Sat with ${preset.label.toLowerCase()} for ${duration} minutes.`,
        category: "Focus",
      });
      toast.success("Kept in your Memory Garden");
      onClose?.();
    } catch (_) {
      toast.error("Couldn't save just now.");
    }
  };

  return (
    <>
      {/* Compact "keep going" widget — shown when the user minimises during a
          running session. Fixed bottom-right, does not block the app. */}
      {minimized && phase === "running" && (
        <MinimizedWidget
          remaining={remaining}
          paused={paused}
          ambienceLabel={AMBIENCES.find(a => a.key === ambience)?.label || ""}
          onExpand={() => setMinimized(false)}
          onTogglePause={() => setPaused(p => !p)}
          onEnd={endEarly}
        />
      )}

      {/* Audio element lives outside the visible tree so it keeps playing even
          when the overlay is minimised. */}
      <audio ref={audioRef} src={audioUrl || undefined} loop preload="auto"/>

      {!minimized && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center overflow-hidden bg-[#0b0f14]"
          data-testid="focus-mode"
        >
          {/* Serene backdrop — breathing radial gradients */}
          <BreathingBackdrop />

          {/* Top-right controls: minimise (running only) + close */}
          <div className="absolute top-5 right-5 z-20 flex items-center gap-1.5">
            {phase === "running" && (
              <button
                type="button"
                onClick={() => setMinimized(true)}
                className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white/80 hover:text-white transition"
                aria-label="Minimise focus mode"
                title="Minimise (Esc)"
                data-testid="focus-mode-minimize"
              >
                <L.Minus size={18}/>
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                if (phase === "running") {
                  if (confirm("End this session and close focus mode?")) {
                    finishSession();
                    onClose?.();
                  }
                } else {
                  onClose?.();
                }
              }}
              className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white/80 hover:text-white transition"
              aria-label="Close focus mode"
              title="Close"
              data-testid="focus-mode-close-x"
            >
              <L.X size={18}/>
            </button>
          </div>

          {/* Content */}
          <div className="relative z-10 w-full max-w-2xl px-6 text-white">
        {phase === "setup" && (
          <div className="space-y-8 fade-up">
            <div className="text-center">
              <div className="text-[10px] uppercase tracking-[0.32em] text-white/50 font-semibold">Focus mode</div>
              <div className="mt-2 text-4xl sm:text-5xl font-display leading-tight">A quiet moment, held for you.</div>
              <div className="mt-3 text-[13px] text-white/60 max-w-md mx-auto">
                Pick a duration and an ambience. Rammy will keep time — you just breathe.
              </div>
            </div>

            <div>
              <div className="text-[10px] uppercase tracking-[0.24em] text-white/50 font-semibold mb-3">Duration</div>
              <div className="grid grid-cols-3 gap-2">
                {DURATIONS.map(d => (
                  <button
                    key={d.min}
                    type="button"
                    onClick={() => setDuration(d.min)}
                    className={`rounded-2xl border p-4 text-left transition
                      ${duration === d.min
                        ? "border-[#48e0c8] bg-[#48e0c8]/10"
                        : "border-white/10 hover:border-white/30"}`}
                    data-testid={`focus-mode-duration-${d.min}`}
                  >
                    <div className="font-medium">{d.label}</div>
                    <div className="text-[11px] text-white/50 mt-0.5">{d.copy}</div>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <div className="text-[10px] uppercase tracking-[0.24em] text-white/50 font-semibold mb-3">Ambience</div>
              <div className="flex flex-wrap gap-2">
                {AMBIENCES.map(a => (
                  <button
                    key={a.key}
                    type="button"
                    onClick={() => setAmbience(a.key)}
                    className={`text-[13px] rounded-full px-3 py-1.5 border transition
                      ${ambience === a.key
                        ? "border-[#48e0c8] bg-[#48e0c8]/15 text-white"
                        : "border-white/10 text-white/70 hover:border-white/30"}`}
                    data-testid={`focus-mode-ambience-${a.key}`}
                  >
                    {a.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-between pt-2">
              <button
                type="button"
                onClick={onClose}
                className="text-[13px] text-white/60 hover:text-white"
                data-testid="focus-mode-cancel"
              >Not now</button>
              <button
                type="button"
                onClick={start}
                className="inline-flex items-center gap-2 rounded-full bg-[#48e0c8] text-[#0b0f14] px-6 py-3 font-medium hover:bg-[var(--rm-surface)] transition"
                data-testid="focus-mode-start"
              >
                <L.Play size={16}/> Begin
              </button>
            </div>
          </div>
        )}

        {phase === "preparing" && (
          <div className="text-center space-y-3" data-testid="focus-mode-preparing">
            <div className="rammy-orb rammy-orb--lg mx-auto" data-mood="thinking" aria-hidden />
            <div className="text-[13px] text-white/60 italic mt-4">Laying down the ambience…</div>
          </div>
        )}

        {phase === "running" && (
          <div className="text-center space-y-8 fade-up">
            <div className="rammy-orb rammy-orb--lg mx-auto" data-mood={paused ? "idle" : "speaking"} aria-hidden />
            <div className="text-[10px] uppercase tracking-[0.32em] text-white/40 font-semibold">In focus</div>
            <div className="font-mono text-[96px] leading-none tabular-nums tracking-tight" data-testid="focus-mode-timer">
              {fmt(remaining)}
            </div>
            <div className="text-[13px] text-white/50">
              {AMBIENCES.find(a => a.key === ambience)?.label}
              {paused && " · paused"}
            </div>
            <div className="flex items-center justify-center gap-3 pt-4">
              <button
                type="button"
                onClick={() => setPaused(p => !p)}
                className="w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"
                aria-label={paused ? "Resume" : "Pause"}
                data-testid="focus-mode-toggle"
              >
                {paused ? <L.Play size={18}/> : <L.Pause size={18}/>}
              </button>
              <button
                type="button"
                onClick={endEarly}
                className="text-[13px] text-white/60 hover:text-white ml-2"
                data-testid="focus-mode-end"
              >End early</button>
            </div>
          </div>
        )}

        {phase === "done" && (
          <div className="text-center space-y-6 fade-up" data-testid="focus-mode-done">
            <div className="rammy-orb rammy-orb--lg mx-auto" data-mood="idle" aria-hidden />
            <div className="space-y-2">
              <div className="text-[10px] uppercase tracking-[0.32em] text-white/50 font-semibold">Nicely done</div>
              <div className="text-3xl font-display">You held the room for {duration} minutes.</div>
              <div className="text-[13px] text-white/60 max-w-md mx-auto">
                Keep this session as a memory? Rammy will remember how you like to focus.
              </div>
            </div>
            <div className="flex items-center justify-center gap-3">
              <button
                type="button"
                onClick={onClose}
                className="text-[13px] text-white/60 hover:text-white"
                data-testid="focus-mode-close"
              >Close</button>
              <button
                type="button"
                onClick={saveMemory}
                className="inline-flex items-center gap-2 rounded-full bg-[#48e0c8] text-[#0b0f14] px-5 py-2.5 font-medium hover:bg-[var(--rm-surface)] transition"
                data-testid="focus-mode-save"
              >
                <L.BookmarkPlus size={15}/> Keep as memory
              </button>
            </div>
          </div>
        )}
      </div>
        </div>
      )}
    </>
  );
}

/** Small floating widget shown when the user minimises a running session.
 *  Anchored bottom-right so it does not overlap the chat composer. */
function MinimizedWidget({ remaining, paused, ambienceLabel, onExpand, onTogglePause, onEnd }) {
  return (
    <div
      className="fixed right-5 bottom-24 z-40 flex items-center gap-2 rounded-full bg-[#0b0f14]/95 text-white pl-2 pr-2 py-2 shadow-[0_12px_32px_rgba(17,19,24,0.35)] border border-white/10 backdrop-blur"
      data-testid="focus-mode-minimized"
      role="region"
      aria-label="Focus session in progress"
    >
      <button
        type="button"
        onClick={onExpand}
        className="flex items-center gap-2 pl-1.5 pr-2 py-1 rounded-full hover:bg-white/10 transition"
        aria-label="Expand focus mode"
        title="Expand"
        data-testid="focus-mode-expand"
      >
        <span className="w-6 h-6 rounded-full bg-[var(--rammy-cyan,#48e0c8)]/25 flex items-center justify-center">
          <L.Sparkles size={13} className="text-[var(--rammy-cyan,#48e0c8)]"/>
        </span>
        <span className="font-mono text-[13px] tabular-nums" data-testid="focus-mode-mini-timer">
          {`${Math.floor(remaining / 60)}:${String(remaining % 60).padStart(2, "0")}`}
        </span>
        <span className="text-[11px] text-white/50 hidden sm:inline max-w-[120px] truncate">
          {ambienceLabel}{paused ? " · paused" : ""}
        </span>
      </button>
      <div className="w-px h-5 bg-white/10"/>
      <button
        type="button"
        onClick={onTogglePause}
        className="w-8 h-8 rounded-full hover:bg-white/10 flex items-center justify-center text-white/80 hover:text-white transition"
        aria-label={paused ? "Resume" : "Pause"}
        title={paused ? "Resume" : "Pause"}
        data-testid="focus-mode-mini-toggle"
      >
        {paused ? <L.Play size={14}/> : <L.Pause size={14}/>}
      </button>
      <button
        type="button"
        onClick={onEnd}
        className="w-8 h-8 rounded-full hover:bg-white/10 flex items-center justify-center text-white/60 hover:text-white transition"
        aria-label="End session"
        title="End"
        data-testid="focus-mode-mini-end"
      >
        <L.X size={14}/>
      </button>
    </div>
  );
}

/** A pair of slowly-breathing radial gradients. Pure CSS; no external deps. */
function BreathingBackdrop() {
  return (
    <>
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(60% 55% at 30% 40%, rgba(72,224,200,0.15) 0%, rgba(11,15,20,0) 60%), " +
            "radial-gradient(50% 45% at 75% 70%, rgba(120,140,255,0.12) 0%, rgba(11,15,20,0) 55%), " +
            "radial-gradient(80% 80% at 50% 50%, rgba(11,15,20,0) 0%, #0b0f14 100%)",
          animation: "focusBreath 9s ease-in-out infinite",
        }}
      />
      <style>{`@keyframes focusBreath {
        0%   { transform: scale(1);     opacity: 0.85; }
        50%  { transform: scale(1.06);  opacity: 1;    }
        100% { transform: scale(1);     opacity: 0.85; }
      }`}</style>
    </>
  );
}
